// types
import type { CommunicationTemplateType } from '@/types/domain/communicationTemplate'

/**
 * Supported placeholder categories shown in the template editor.
 */
type CommunicationTemplatePlaceholderCategory =
  | 'Email'
  | 'SMS'
  | 'Customer'
  | 'Agreement'
  | 'Vehicle'
  | 'Dealership'
  | 'User'

/**
 * Metadata describing an available communication template placeholder.
 */
interface CommunicationTemplatePlaceholderOption {
  category: CommunicationTemplatePlaceholderCategory
  label: string
  token: string
  types: CommunicationTemplateType[]
}

/**
 * Shared placeholder token constants used by editor and runtime replacement flows.
 */
const communicationTemplatePlaceholderTokens = {
  emailGreeting: '{{email_greeting}}',
  emailSignOff: '{{email_sign_off}}',
  onBehalfOf: '{{on_behalf_of}}',
  smsGreeting: '{{sms_greeting}}',
  smsSignOff: '{{sms_sign_off}}',
  customerName: '{{customer_name}}',
  customerForename: '{{customer_forename}}',
  customerSurname: '{{customer_surname}}',
  agreementId: '{{agreement_id}}',
  agreementEndDate: '{{agreement_end_date}}',
  agreementFinalRepayment: '{{agreement_final_repayment}}',
  agreementProductType: '{{agreement_product_type}}',
  manufacturerBrandColor: '{{manufacturer_brand_color}}',
  agreementMonthsRemaining: '{{agreement_months_remaining}}',
  agreementProduct: '{{agreement_product}}',
  vehicle: '{{vehicle}}',
  vehicleImageUrl: '{{vehicle_image_url}}',
  vehicleCategory: '{{vehicle_category}}',
  vehicleVrm: '{{vehicle_vrm}}',
  vehicleMake: '{{vehicle_make}}',
  vehicleModel: '{{vehicle_model}}',
  odometerAllowanceYearly: '{{odometer_allowance_yearly}}',
  odometerUnit: '{{odometer_unit}}',
  odometerExpected: '{{odometer_expected}}',
  dealershipName: '{{dealership_name}}',
  dealershipAssignedPhoneNumber: '{{dealership_assigned_phone_number}}',
  dealershipAssignedEmail: '{{dealership_assigned_email}}',
  dealershipAssignedSmsNumber: '{{dealership_assigned_sms_number}}',
  userName: '{{user_name}}',
  userForename: '{{user_forename}}',
  userSurname: '{{user_surname}}'
} as const

/**
 * Placeholder metadata shown in the editor dropdown.
 */
const communicationTemplatePlaceholderOptions: CommunicationTemplatePlaceholderOption[] = [
  {
    category: 'Email',
    label: 'Email greeting',
    token: communicationTemplatePlaceholderTokens.emailGreeting,
    types: ['email']
  },
  {
    category: 'Email',
    label: 'Email sign off',
    token: communicationTemplatePlaceholderTokens.emailSignOff,
    types: ['email']
  },
  {
    category: 'Email',
    label: 'On behalf of',
    token: communicationTemplatePlaceholderTokens.onBehalfOf,
    types: ['email']
  },
  {
    category: 'SMS',
    label: 'SMS greeting',
    token: communicationTemplatePlaceholderTokens.smsGreeting,
    types: ['sms']
  },
  {
    category: 'SMS',
    label: 'SMS sign off',
    token: communicationTemplatePlaceholderTokens.smsSignOff,
    types: ['sms']
  },
  {
    category: 'Customer',
    label: 'Customer name',
    token: communicationTemplatePlaceholderTokens.customerName,
    types: ['email', 'sms']
  },
  {
    category: 'Customer',
    label: 'Customer forename',
    token: communicationTemplatePlaceholderTokens.customerForename,
    types: ['email', 'sms']
  },
  {
    category: 'Customer',
    label: 'Customer surname',
    token: communicationTemplatePlaceholderTokens.customerSurname,
    types: ['email', 'sms']
  },
  {
    category: 'Agreement',
    label: 'Agreement ID',
    token: communicationTemplatePlaceholderTokens.agreementId,
    types: ['email', 'sms']
  },
  {
    category: 'Agreement',
    label: 'Agreement end date',
    token: communicationTemplatePlaceholderTokens.agreementEndDate,
    types: ['email', 'sms']
  },
  {
    category: 'Agreement',
    label: 'Final repayment',
    token: communicationTemplatePlaceholderTokens.agreementFinalRepayment,
    types: ['email', 'sms']
  },
  {
    category: 'Agreement',
    label: 'Product type',
    token: communicationTemplatePlaceholderTokens.agreementProductType,
    types: ['email', 'sms']
  },
  {
    category: 'Agreement',
    label: 'Brand colour',
    token: communicationTemplatePlaceholderTokens.manufacturerBrandColor,
    types: ['email']
  },
  {
    category: 'Agreement',
    label: 'Months remaining',
    token: communicationTemplatePlaceholderTokens.agreementMonthsRemaining,
    types: ['email', 'sms']
  },
  {
    category: 'Agreement',
    label: 'Agreement product',
    token: communicationTemplatePlaceholderTokens.agreementProduct,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Vehicle name',
    token: communicationTemplatePlaceholderTokens.vehicle,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Vehicle image URL',
    token: communicationTemplatePlaceholderTokens.vehicleImageUrl,
    types: ['email']
  },
  {
    category: 'Vehicle',
    label: 'Vehicle category',
    token: communicationTemplatePlaceholderTokens.vehicleCategory,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Vehicle VRM',
    token: communicationTemplatePlaceholderTokens.vehicleVrm,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Vehicle make',
    token: communicationTemplatePlaceholderTokens.vehicleMake,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Vehicle model',
    token: communicationTemplatePlaceholderTokens.vehicleModel,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Yearly mileage allowance',
    token: communicationTemplatePlaceholderTokens.odometerAllowanceYearly,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Odometer unit',
    token: communicationTemplatePlaceholderTokens.odometerUnit,
    types: ['email', 'sms']
  },
  {
    category: 'Vehicle',
    label: 'Expected mileage',
    token: communicationTemplatePlaceholderTokens.odometerExpected,
    types: ['email', 'sms']
  },
  {
    category: 'Dealership',
    label: 'Dealership name',
    token: communicationTemplatePlaceholderTokens.dealershipName,
    types: ['email', 'sms']
  },
  {
    category: 'Dealership',
    label: 'Assigned phone number',
    token: communicationTemplatePlaceholderTokens.dealershipAssignedPhoneNumber,
    types: ['email', 'sms']
  },
  {
    category: 'Dealership',
    label: 'Assigned email',
    token: communicationTemplatePlaceholderTokens.dealershipAssignedEmail,
    types: ['email']
  },
  {
    category: 'Dealership',
    label: 'Assigned SMS number',
    token: communicationTemplatePlaceholderTokens.dealershipAssignedSmsNumber,
    types: ['sms']
  },
  {
    category: 'User',
    label: 'User name',
    token: communicationTemplatePlaceholderTokens.userName,
    types: ['email', 'sms']
  },
  {
    category: 'User',
    label: 'User forename',
    token: communicationTemplatePlaceholderTokens.userForename,
    types: ['email', 'sms']
  },
  {
    category: 'User',
    label: 'User surname',
    token: communicationTemplatePlaceholderTokens.userSurname,
    types: ['email', 'sms']
  }
]

/**
 * Returns the placeholders relevant to the selected template type.
 */
function getCommunicationTemplatePlaceholderOptions(
  type: CommunicationTemplateType | null
): CommunicationTemplatePlaceholderOption[] {
  if (type == null) {
    return communicationTemplatePlaceholderOptions
  }

  return communicationTemplatePlaceholderOptions.filter(option => option.types.includes(type))
}

export {
  communicationTemplatePlaceholderOptions,
  communicationTemplatePlaceholderTokens,
  getCommunicationTemplatePlaceholderOptions
}

export type { CommunicationTemplatePlaceholderCategory, CommunicationTemplatePlaceholderOption }
